"use client";

import type React from "react";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { supabase } from "../supabaseClient";
import type { Session, User } from "@supabase/supabase-js";
import type { Profile } from "../types/types";

interface AuthContextType {
  session: Session | null;
  user: User | null;
  userId: string | null;
  profile: Profile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  // Use refs to track subscription state and prevent duplicate subscriptions
  const profileSubscriptionRef = useRef<any>(null);
  const isInitializedRef = useRef(false);

  const fetchProfileInternal = useCallback(
    async (currentUserId: string | null) => {
      if (!currentUserId) {
        setProfile(null);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", currentUserId)
          .maybeSingle(); // Use maybeSingle() instead of single() to handle no results gracefully

        if (error) {
          console.warn(
            `[AuthContext] Error fetching profile: ${error.message}`
          );
          setProfile(null);
          return;
        }

        if (!data) {
          console.warn(
            `[AuthContext] No profile found for user: ${currentUserId}, creating new profile...`
          );

          try {
            // Get user data from Supabase auth to populate profile
            const {
              data: { user },
              error: userError,
            } = await supabase.auth.getUser();

            if (userError || !user) {
              console.error(
                `[AuthContext] Error getting user data: ${userError?.message}`
              );
              setProfile(null);
              return;
            }

            // Create new profile with user's email and basic info
            const newProfileData = {
              id: currentUserId,
              email: user.email,
              full_name: user.user_metadata?.full_name || null,
              username: user.user_metadata?.username || null,
              company_name: null,
              subscription_status: null,
              subscription_plan: null,
              subscription_base_credits: 0,
              subscription_remaining_credits: 0,
              purchased_remaining_credits: 0,
            };

            const { data: newProfile, error: createError } = await supabase
              .from("profiles")
              .insert([newProfileData])
              .select()
              .single();

            if (createError) {
              console.error(
                `[AuthContext] Error creating profile: ${createError.message}`
              );
              setProfile(null);
              return;
            }
            setProfile(newProfile as Profile);
            return;
          } catch (createError: any) {
            console.error(
              `[AuthContext] Critical error creating profile: ${createError.message}`
            );
            setProfile(null);
            return;
          }
        }

        setProfile(data as Profile);
      } catch (error: any) {
        console.error(
          `[AuthContext] Critical error fetching profile: ${error.message}`
        );
        setProfile(null);
      }
    },
    []
  );

  const refreshProfile = useCallback(async () => {
    if (userId) {
      await fetchProfileInternal(userId);
    }
  }, [userId, fetchProfileInternal]);

  // Cleanup function for profile subscription
  const cleanupProfileSubscription = useCallback(() => {
    if (profileSubscriptionRef.current) {
      supabase.removeChannel(profileSubscriptionRef.current);
      profileSubscriptionRef.current = null;
    }
  }, []);

  // Setup profile subscription
  const setupProfileSubscription = useCallback(
    (currentUserId: string) => {
      // Clean up existing subscription first
      cleanupProfileSubscription();

      profileSubscriptionRef.current = supabase
        .channel(`profile_changes_for_${currentUserId}`)
        .on(
          "postgres_changes",
          {
            event: "UPDATE",
            schema: "public",
            table: "profiles",
            filter: `id=eq.${currentUserId}`,
          },
          (payload) => {
            const newProfile = payload.new as Profile;
            setProfile(newProfile);
          }
        )
        .subscribe((status, err) => {});
    },
    [cleanupProfileSubscription]
  );

  useEffect(() => {
    // Prevent multiple initializations
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    const getInitialSession = async () => {
      setLoading(true);

      try {
        const {
          data: { session: initialSession },
        } = await supabase.auth.getSession();

        setSession(initialSession);
        const initialUser = initialSession?.user ?? null;
        setUser(initialUser);
        const initialUserId = initialUser?.id ?? null;
        setUserId(initialUserId);

        if (initialUserId) {
          await fetchProfileInternal(initialUserId);
          setupProfileSubscription(initialUserId);
        } else {
          setProfile(null);
        }
      } catch (error: any) {
        console.error("[AuthContext] Error getting initial session:", error);
      } finally {
        setLoading(false);
      }
    };

    getInitialSession();

    const { data: authListener } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        // Skip INITIAL_SESSION as it's handled by getInitialSession
        if (event === "INITIAL_SESSION") {
          return;
        }

        setLoading(true);
        setSession(currentSession);
        const currentUser = currentSession?.user ?? null;
        setUser(currentUser);
        const currentUserId = currentUser?.id ?? null;
        setUserId(currentUserId);

        if (currentUserId) {
          await fetchProfileInternal(currentUserId);
          setupProfileSubscription(currentUserId);
        } else {
          setProfile(null);
          cleanupProfileSubscription();
        }

        setLoading(false);
      }
    );

    return () => {
      authListener?.subscription.unsubscribe();
      cleanupProfileSubscription();
      isInitializedRef.current = false;
    };
  }, []); // Empty dependency array - this should only run once

  return (
    <AuthContext.Provider
      value={{ session, user, userId, profile, loading, refreshProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
